import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { type ComparatorType } from '@repo/advanced-filters';
import { LeadSystemAttributeSchemaName } from '@repo/crm-atoms';

import { LOAD_STORED_STATE, type LoadStoredStateAction } from './constants';

export type TimeFilterOptionsType = {
  filterType?: ComparatorType;
  offsetValue?: string;
  fromTime?: string;
  toTime?: string;
};

export type ConditionKeyType = {
  value?: string;
  label?: string;
  entityType?: string;
  dataType?: string;
  schemaName?: string;
};

export interface ConditionsType {
  conditionKey: ConditionKeyType;
  conditionComparator: string;
  conditionValue: {
    value: [{
      value: string;
      label: string;
    }] | {
      value: { timeFilter: TimeFilterOptionsType },
      label: string;
    }
  };
  level: number;
}
export type SortConfig = {
  sortField: string;
  sortOrder: 'asc' | 'desc';
};

export interface SecondaryFilterSliceType {
  conditions: Record<string, ConditionsType | undefined>;
  searchText: string;
  sortConfig?: SortConfig;
}

export const DEFAULT_SORT_CONFIG: SortConfig = {
  sortField: LeadSystemAttributeSchemaName.createdOn,
  sortOrder: 'desc',
};

const initialState: SecondaryFilterSliceType = {
  conditions: {},
  searchText: '',
  sortConfig: DEFAULT_SORT_CONFIG,
};

export const secondaryFilterSlice = createSlice({
  name: 'manageLeads/SecondaryFilter',
  initialState,
  reducers: {
    addSecondaryFilterCondition: (state, action: PayloadAction<{
      type: string,
      condition: ConditionsType | undefined,
    }>) => {
      state.conditions[action.payload.type] = action.payload.condition;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setSortConfig: (state, action: PayloadAction<{
      sortField?: string,
      sortDirection?: 'ascend' | 'descend',
    }>) => {
      let sortOrder: 'asc' | 'desc' = 'desc';
      if (action.payload.sortDirection === 'ascend') {
        sortOrder = 'asc';
      } else if (action.payload.sortDirection === 'descend') {
        sortOrder = 'desc';
      }
      state.sortConfig = {
        sortField: action.payload.sortField || LeadSystemAttributeSchemaName.createdOn,
        sortOrder,
      };
    },
    resetSecondaryFilter: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(LOAD_STORED_STATE, (_, action: LoadStoredStateAction) => ({
      ...initialState,
      ...action.payload.secondaryFilter,
    }));
  },
});

export const {
  addSecondaryFilterCondition,
  setSearchText,
  setSortConfig,
  resetSecondaryFilter,
} = secondaryFilterSlice.actions;

export default secondaryFilterSlice.reducer;
